import React from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Avatar, Box, DataListItem, Highlight, Icon } from '@node-space/ui-components'
import { RelationshipType } from 'types/contacts'
import { contactAtom } from '../atoms/contactAtoms'
import { isContactEntityCompany, isContactEntityIndividual } from '../utils/utils'

export const ContactInfo = () => {
  const { t } = useTranslation()
  const contact = useAtomValue(contactAtom)
  const { name, description, relationshipType, entity } = contact || {}
  const address = entity?.address
  const isCompany = isContactEntityCompany(entity)
  const isIndividual = isContactEntityIndividual(entity)
  const isSelfOwnedCompany = relationshipType === RelationshipType.SELF_OWNED && isCompany

  return (
    <>
      <Box flexCol padding={16} gap={32}>
        <Box flexCol align="center" gapY={12}>
          {name && <Avatar size="large" type="entity" name={name || ''} />}
          <h3 className="text-h3 text-foreground-prominent text-center">{name}</h3>
          {description && (
            <p className="text-body-regular text-foreground-subdued text-center">{description}</p>
          )}
          <Box flex gapX={8} paddingB={4}>
            {(isCompany || isIndividual) && (
              <Highlight
                iconElement={
                  isCompany ? (
                    <Icon name="BuildingOffice" size="small" aria-label={t('contacts.company')} />
                  ) : (
                    <Icon name="UserCircle" size="small" aria-label={t('contacts.individual')} />
                  )
                }
                type="neutral"
                variant="emphasis"
              >
                {isCompany ? t('contacts.company') : t('contacts.individual')}
              </Highlight>
            )}
            {isSelfOwnedCompany && (
              <Highlight type="feature" variant="emphasis">
                {t('contacts.myCompany')}
              </Highlight>
            )}
          </Box>
        </Box>
      </Box>

      {isIndividual && entity?.firstName && (
        <DataListItem
          id="first-name"
          title={t('firstName')}
          body={entity?.firstName}
          layout="vertical"
        />
      )}

      {isIndividual && entity?.lastName && (
        <DataListItem
          id="last-name"
          title={t('lastName')}
          body={entity?.lastName}
          layout="vertical"
        />
      )}

      {isIndividual && entity?.dateOfBirth && (
        <DataListItem
          id="date-of-birth"
          title={t('contacts.dateOfBirth')}
          body={entity?.dateOfBirth}
          layout="vertical"
        />
      )}

      {isCompany && entity?.dateOfIncorporation && (
        <DataListItem
          id="date-of-incorporation"
          title={t('contacts.dateOfIncorporation')}
          body={entity?.dateOfIncorporation}
          layout="vertical"
        />
      )}

      {address && (
        <>
          <DataListItem
            id="address"
            title={t('contacts.address')}
            body={[
              address?.addressLine1,
              address?.addressLine2,
              address?.city,
              address?.region,
              address?.country,
              address?.postCode,
            ]
              ?.filter(Boolean)
              ?.join(', ')}
            layout="vertical"
          />
        </>
      )}

      {isCompany && entity?.registrationNumber && (
        <DataListItem
          id="registration-number"
          title={t('contacts.registrationNumber')}
          body={entity?.registrationNumber}
          layout="vertical"
        />
      )}
    </>
  )
}
